import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RadixSelect,
  toast,
  type SelectOption,
} from '@/components/ui';
import { useCreateSenderNumberMutation } from '../hooks/useIntegrationQuery';
import type { SenderPurpose } from '../types/notificationIntegration';

export interface RegisterSenderNumberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 발신번호 등록 — 발신번호 + 용도 + 설명 입력 후 생성(POST /sender-numbers)으로 즉시 등록 완료.
 * 별도 인증코드(verify-send/confirm) 단계 없음: 생성 시 status=ACTIVE 로 바로 사용 가능하며,
 * 실발송용 통신사 사전등록은 BizTalk 콘솔(외부)에서 관리한다.
 */
export const RegisterSenderNumberModal = ({ open, onOpenChange }: RegisterSenderNumberModalProps) => {
  const { t } = useTranslation('notifications');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [description, setDescription] = useState('');
  const [purpose, setPurpose] = useState<SenderPurpose>('SMS');

  const create = useCreateSenderNumberMutation();

  useEffect(() => {
    if (!open) {
      setPhoneNumber('');
      setDescription('');
      setPurpose('SMS');
    }
  }, [open]);

  const purposeOptions: ReadonlyArray<SelectOption> = [
    { value: 'SMS', label: t('integration.sender.purpose.SMS', '문자 발송') },
    { value: 'ALIMTALK_FALLBACK', label: t('integration.sender.purpose.ALIMTALK_FALLBACK', '알림톡 대체') },
    { value: 'BOTH', label: t('integration.sender.purpose.BOTH', '문자·알림톡 대체') },
  ];

  // 발신번호 생성 → 즉시 등록 완료. 인증코드 단계 없음.
  const onCreate = () => {
    if (!phoneNumber.trim()) return;
    create.mutate(
      { phoneNumber: phoneNumber.trim(), description: description.trim() || null, purpose },
      {
        onSuccess: () => {
          toast.success(t('integration.sender.toast.createSuccess', '발신번호가 등록되었습니다.'));
          onOpenChange(false);
        },
        onError: (e) => {
          if (e.status === 409) {
            toast.error(t('integration.sender.toast.duplicate', '이미 등록된 발신번호입니다.'));
            return;
          }
          toast.error(e.message ?? t('integration.sender.toast.createFail', '발신번호 등록에 실패했습니다.'));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('integration.sender.register.title', '발신번호 등록')}</DialogTitle>
          <DialogDescription>
            {t('integration.sender.description', '문자(SMS/LMS/MMS) 발송에 사용할 발신번호를 등록합니다.')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('integration.sender.register.number', '발신번호')}
            </span>
            <Input
              type="tel"
              size="md"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder={t('integration.sender.register.numberPlaceholder', '010-0000-0000')}
              aria-label={t('integration.sender.register.number', '발신번호')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('integration.sender.register.purpose', '용도')}
            </span>
            <RadixSelect
              value={purpose}
              onChange={(v) => setPurpose(v as SenderPurpose)}
              options={purposeOptions}
              aria-label={t('integration.sender.register.purpose', '용도')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('integration.sender.register.label', '설명')}
            </span>
            <Input
              size="md"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('integration.sender.register.labelPlaceholder', '예: 고객센터 대표번호')}
              aria-label={t('integration.sender.register.label', '설명')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('common.cancel', '취소')}
          </Button>
          <Button size="sm" disabled={!phoneNumber.trim() || create.isPending} onClick={onCreate}>
            {create.isPending
              ? t('integration.sender.register.submitting', '등록 중…')
              : t('integration.sender.register.submit', '등록')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
